import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Heading,
  IconButton,
  Card,
  Badge,
  Alert,
  Skeleton,
  SimpleGrid,
  Table,
  NativeSelect,
  Tooltip,
  Portal,
} from '@chakra-ui/react';
import { Chart, useChart } from '@chakra-ui/charts';
import {
  CartesianGrid,
  Line,
  LineChart,
  Legend,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RefreshCw, User, Users, Clock, TrendingUp, Ban, ExternalLink } from 'lucide-react';
import { formatMoani } from '../utils/moaniUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';

export default function Dashboard() {
  const [recentlyBanned, setRecentlyBanned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerActivity, setPlayerActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const SHOW_CHARTS = false;

  const fetchRecentlyBanned = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/dashboard/recently-banned?limit=20');
      setRecentlyBanned(response.data.users || []);
    } catch (err) {
      console.error('Error fetching recently banned users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerActivity = async () => {
    setActivityLoading(true);
    try {
      const response = await api.get(`/dashboard/player-activity?days=${days}`);
      setPlayerActivity(response.data);
    } catch (err) {
      console.error('Error fetching player activity:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentlyBanned();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchPlayerActivity();
    }
  }, [days, isAdmin]);

  const handleUserClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  const handleRefresh = () => {
    fetchRecentlyBanned();
    if (isAdmin) fetchPlayerActivity();
  };

  // Chart data: Unique Users + Active Players only (no sessions)
  const chartData =
    playerActivity?.sessionsPerDay?.map((session) => ({
      date: new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      uniqueUsers: session.uniqueUsers,
    })) || [];

  const chart = useChart({
    data: chartData,
    series: [{ name: 'uniqueUsers', color: '#ff4081', label: 'Unique Users' }],
  });

  const StatCardSkeleton = () => (
    <Card.Root variant="outline" p="4">
      <Card.Body>
        <HStack gap="3" align="center">
          <Skeleton w="10" h="10" borderRadius="md" />
          <VStack gap="1" align="start">
            <Skeleton h="6" w="16" />
            <Skeleton h="4" w="24" />
          </VStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );

  const StatCard = ({ icon: Icon, value, label, iconBg }) => (
    <Card.Root variant="outline" p="4" bg="#1a1a1a" borderColor="rgba(0,229,255,0.2)">
      <Card.Body>
        <HStack gap="3" align="center">
          <Box p="2" borderRadius="md" bg={iconBg} color="white" display="flex" alignItems="center" justifyContent="center">
            <Icon size={24} />
          </Box>
          <VStack gap="0" align="start">
            <Text fontSize="xl" fontWeight="bold">
              {(value ?? 0).toLocaleString()}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {label}
            </Text>
          </VStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );

  return (
    <Box p="6">
      <HStack justify="space-between" align="center" mb="6">
        <Heading size="lg">Dashboard</Heading>
        <HStack gap="2" align="center">
          {isAdmin && (
            <>
              <NativeSelect.Root size="sm" w="140px">
                <NativeSelect.Field
                  value={String(days)}
                  onChange={(e) => setDays(Number(e.currentTarget.value))}
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <IconButton
                aria-label="Refresh"
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={loading || activityLoading}
              >
                <RefreshCw size={18} />
              </IconButton>
            </>
          )}
        </HStack>
      </HStack>

      {/* Player Activity Stats - Admin only */}
      {isAdmin && (
        <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} gap="4" mb="6">
          {activityLoading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <StatCardSkeleton key={i} />
              ))}
            </>
          ) : (
            <>
              <StatCard
                icon={Users}
                value={playerActivity?.lastHourActive}
                label="Online (1h)"
                iconBg="#00e676"
              />
              <StatCard
                icon={Users}
                value={playerActivity?.currentActive}
                label="Active Now (24h)"
                iconBg="#00e5ff"
              />
              <StatCard
                icon={Clock}
                value={playerActivity?.todayStats?.sessionsToday}
                label="Sessions Today"
                iconBg="#2196f3"
              />
              <StatCard
                icon={User}
                value={playerActivity?.todayStats?.uniqueUsersToday}
                label="Unique Users Today"
                iconBg="#ff4081"
              />
              <StatCard
                icon={TrendingUp}
                value={
                  playerActivity?.sessionsPerDay?.length
                    ? Math.round(
                        playerActivity.sessionsPerDay.reduce(
                          (sum, d) => sum + Number(d.sessionCount || 0),
                          0
                        ) / playerActivity.sessionsPerDay.length
                      )
                    : 0
                }
                label="Avg Sessions/Day"
                iconBg="#ffab00"
              />
            </>
          )}
        </SimpleGrid>
      )}

      {/* Chart - Admin only (disabled for now) */}
      {isAdmin && SHOW_CHARTS && (
        <Card.Root variant="outline" p="6" mb="6" bg="#1a1a1a" borderColor="rgba(0,229,255,0.2)">
          <Card.Header>
            <Heading size="md">Unique Users Over Time</Heading>
          </Card.Header>
          <Card.Body>
            {activityLoading ? (
              <Skeleton h="300px" borderRadius="md" />
            ) : chartData.length > 0 ? (
              <Chart.Root maxH="300px" chart={chart}>
                <LineChart data={chart.data}>
                  <CartesianGrid stroke="rgba(255,255,255,0.1)" vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    axisLine={false}
                    tickLine={false}
                    dataKey={chart.key('date')}
                    stroke="rgba(255,255,255,0.7)"
                    tick={{ fill: 'rgba(255,255,255,0.7)' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    stroke="rgba(255,255,255,0.7)"
                    tick={{ fill: 'rgba(255,255,255,0.7)' }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(0, 229, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#ffffff',
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey={chart.key('uniqueUsers')}
                    stroke="#ff4081"
                    strokeWidth={2}
                    name="Unique Users"
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </Chart.Root>
            ) : (
              <Box py="12" textAlign="center" color="fg.muted">
                No chart data available
              </Box>
            )}
          </Card.Body>
        </Card.Root>
      )}

      {/* Top Players Table - Admin only */}
      {isAdmin && (
        <Card.Root variant="outline" p="6" mb="6" bg="#1a1a1a" borderColor="rgba(0,229,255,0.2)">
          <Card.Header>
            <Heading size="md">Top Active Players ({days} days)</Heading>
          </Card.Header>
          <Card.Body>
            {activityLoading ? (
              <VStack gap="2" align="stretch">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Skeleton key={i} h="12" borderRadius="md" />
                ))}
              </VStack>
            ) : (
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Rank</Table.ColumnHeader>
                    <Table.ColumnHeader>Name</Table.ColumnHeader>
                    <Table.ColumnHeader>Email</Table.ColumnHeader>
                    <Table.ColumnHeader>Wallet</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end">Sessions</Table.ColumnHeader>
                    <Table.ColumnHeader>Last Session</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(!playerActivity?.topPlayers || playerActivity.topPlayers.length === 0) ? (
                    <Table.Row>
                      <Table.Cell colSpan={7} textAlign="center" py="8">
                        <Text color="fg.muted">No player data available</Text>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    playerActivity.topPlayers.map((player, index) => (
                      <Table.Row
                        key={player.id}
                        _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                        cursor="pointer"
                        onClick={() => handleUserClick(player.id)}
                      >
                        <Table.Cell>
                          <Badge colorPalette={index < 3 ? 'cyan' : 'gray'} size="sm">
                            #{index + 1}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">{player.name || '-'}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">{player.email || '-'}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs" fontFamily="monospace">
                            {player.wallet
                              ? `${player.wallet.substring(0, 6)}...${player.wallet.substring(38)}`
                              : '-'}
                          </Text>
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          <Text fontSize="sm" fontWeight="bold">
                            {player.sessionCount?.toLocaleString()}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="xs">
                            {player.lastSession
                              ? new Date(player.lastSession).toLocaleString()
                              : '-'}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="View user"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserClick(player.id);
                            }}
                          >
                            <User size={14} />
                          </IconButton>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            )}
          </Card.Body>
        </Card.Root>
      )}

      {/* Recently Banned Users */}
      <Card.Root variant="outline" p="6" bg="#1a1a1a" borderColor="rgba(0,229,255,0.2)">
        <Card.Header>
          <HStack justify="space-between" align="center">
            <VStack gap="0" align="start">
              <Heading size="md">Recently Banned Users (with MOANI or NFTs)</Heading>
              <Text fontSize="sm" color="fg.muted">
                Users banned in the last period who have MOANI balance or own NFTs
              </Text>
            </VStack>
            <Badge colorPalette="red" size="lg">
              <Ban size={14} />
              <Text as="span" ml="1">
                {recentlyBanned.length}
              </Text>
            </Badge>
          </HStack>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert.Root status="error" colorPalette="red" mb="4">
              <Alert.Indicator />
              <Alert.Title>{error}</Alert.Title>
            </Alert.Root>
          )}

          {loading ? (
            <VStack gap="2" align="stretch">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton key={i} h="12" borderRadius="md" />
              ))}
            </VStack>
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>User ID</Table.ColumnHeader>
                  <Table.ColumnHeader>Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Email</Table.ColumnHeader>
                  <Table.ColumnHeader>Wallet</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="end">MOANI</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">NFTs</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Banned</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {recentlyBanned.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={9} textAlign="center" py="8">
                      <Text color="fg.muted">
                        No recently banned users with MOANI or NFTs found
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  recentlyBanned.map((bannedUser) => (
                    <Table.Row
                      key={bannedUser.id}
                      _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                      cursor="pointer"
                      onClick={() => handleUserClick(bannedUser.id)}
                    >
                      <Table.Cell>
                        <Text fontSize="xs" fontFamily="monospace">
                          {bannedUser.id}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">{bannedUser.name || '-'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs">{bannedUser.email || '-'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs" fontFamily="monospace">
                          {bannedUser.wallet
                            ? `${bannedUser.wallet.substring(0, 6)}...${bannedUser.wallet.substring(38)}`
                            : '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Text fontSize="xs" fontFamily="monospace" color="orange.400">
                          {formatMoani(bannedUser.moaniAmount)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {bannedUser.hasNFTs ? (
                          <Badge colorPalette="blue" size="sm">
                            {bannedUser.nftCount}
                          </Badge>
                        ) : (
                          <Text fontSize="sm" color="fg.muted">
                            -
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap="1">
                          {bannedUser.permaban && (
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Box as="span" color="red.400" cursor="help">
                                  <Ban size={14} />
                                </Box>
                              </Tooltip.Trigger>
                              <Portal>
                                <Tooltip.Positioner>
                                  <Tooltip.Content>Permanently Banned</Tooltip.Content>
                                </Tooltip.Positioner>
                              </Portal>
                            </Tooltip.Root>
                          )}
                          {!bannedUser.permaban && (
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Box as="span" color="orange.400" cursor="help">
                                  <Ban size={14} />
                                </Box>
                              </Tooltip.Trigger>
                              <Portal>
                                <Tooltip.Positioner>
                                  <Tooltip.Content>Temporarily Banned</Tooltip.Content>
                                </Tooltip.Positioner>
                              </Portal>
                            </Tooltip.Root>
                          )}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="xs">
                          {bannedUser.bannedAt
                            ? new Date(bannedUser.bannedAt).toLocaleString()
                            : bannedUser.updatedAt
                              ? new Date(bannedUser.updatedAt).toLocaleString()
                              : '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label="View user"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUserClick(bannedUser.id);
                          }}
                        >
                          <ExternalLink size={14} />
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          )}
        </Card.Body>
      </Card.Root>
    </Box>
  );
}
